import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AssociationWizard, {
  WizardStep
} from '../AssociationWizard/AssociationWizard';
import {
  BillingToggleSection,
  buildBillingLabels
} from '../shared/BillingToggleSection';
import { ConfirmAndRunStep, SummaryRow } from '../shared/ConfirmAndRunStep';
import { RuleViolation } from '../../helpers/types';
import {
  CredentialsStep,
  CredentialsFormValues,
  isCredentialsValid,
  trimCredentials,
  buildCredentialsLabels
} from '../shared/CredentialsStep';

// The parent semi-migration asks for the same inputs as the child
// create-under-parent flow, so it borrows that flow's copy and overrides only
// the handful of strings that name a child (Figma 764:1543, 764:1612).
const CHILD_WIZARD = 'tenantsAssociation.mspChildWizard';
const CHILD_CONFIRM = `${CHILD_WIZARD}.confirmStep`;
const SEMI = 'tenantsAssociation.mspParentWizard.semiMigration';
const SEMI_CREDENTIALS = `${SEMI}.credentialsStep`;
const SEMI_CONFIRM = `${SEMI}.confirmStep`;

export interface MspParentSemiPayload {
  credentials: CredentialsFormValues;
}

export interface MspParentSemiWizardProps {
  /** Upfront validation failures from the last rejected attempt. */
  upfrontErrors?: RuleViolation[];
  credentials: CredentialsFormValues;
  onCredentialsChange: (next: CredentialsFormValues) => void;
  scheduledConfirmed: boolean;
  onScheduledConfirmedChange: (next: boolean) => void;
  onBack: () => void;
  onFinish: (payload: MspParentSemiPayload) => void;
  isFinishing?: boolean;
  onStepChange?: (stepIndex: number) => void;
}

const MspParentSemiWizard = ({
  upfrontErrors,
  credentials,
  onCredentialsChange,
  scheduledConfirmed,
  onScheduledConfirmedChange,
  onBack,
  onFinish,
  isFinishing,
  onStepChange
}: MspParentSemiWizardProps) => {
  const { t } = useTranslation('activity');

  const handleFinish = useCallback(
    () => onFinish({ credentials: trimCredentials(credentials) }),
    [onFinish, credentials]
  );

  const credentialsLabels = useMemo(
    () => ({
      ...buildCredentialsLabels(t, CHILD_WIZARD),
      warningTitle: t(`${SEMI_CREDENTIALS}.WARNING_TITLE`),
      parentUuidHint: t(`${SEMI_CREDENTIALS}.PARENT_UUID_HINT`)
    }),
    [t]
  );

  const summaryRows = useMemo<SummaryRow[]>(
    () => [
      {
        label: t(`${CHILD_CONFIRM}.SUMMARY_MODE`),
        value: t(`${SEMI_CONFIRM}.MODE_VALUE`)
      },
      {
        label: t(`${CHILD_CONFIRM}.SUMMARY_PARENT_UUID`),
        value: credentials.parentUuid
      },
      {
        label: t(`${CHILD_CONFIRM}.SUMMARY_ADMIN_EMAIL`),
        value: credentials.adminEmail
      },
      {
        label: t(`${CHILD_CONFIRM}.SUMMARY_BILLING`),
        value: t(`${CHILD_CONFIRM}.BILLING_MOVE_PAYG`)
      }
    ],
    [t, credentials.parentUuid, credentials.adminEmail]
  );

  // No "Update type: msp → smb, disconnect parent" line: the tenant here IS the
  // parent, so MAPI skips DISCONNECT_FROM_MSP for it (P81-141608). The two
  // create/associate lines say "tenant" rather than "child" for the same reason.
  const actions = useMemo(
    () => [
      t(`${CHILD_CONFIRM}.ACTION_CLOUDINFRA_AUTH`),
      t(`${SEMI_CONFIRM}.ACTION_CREATE_TENANT`),
      t(`${CHILD_CONFIRM}.ACTION_ASSIGN_PAYG`),
      t(`${SEMI_CONFIRM}.ACTION_ASSOCIATE_SASE`),
      t(`${CHILD_CONFIRM}.ACTION_UPDATE_SUBSCRIPTION`)
    ],
    [t]
  );

  const steps = useMemo<WizardStep[]>(
    () => [
      {
        content: (
          <CredentialsStep
            values={credentials}
            onChange={onCredentialsChange}
            labels={credentialsLabels}
          >
            <BillingToggleSection
              labels={buildBillingLabels(t, CHILD_WIZARD)}
            />
          </CredentialsStep>
        ),
        isValid: isCredentialsValid(credentials),
        subtitle: t(`${SEMI_CREDENTIALS}.PAGE_SUBTITLE`)
      },
      {
        content: (
          <ConfirmAndRunStep
            scheduledConfirmed={scheduledConfirmed}
            onConfirmChange={onScheduledConfirmedChange}
            summaryRows={summaryRows}
            actions={actions}
            actionsTitle={t(`${CHILD_CONFIRM}.ACTIONS_TITLE`)}
            confirmCheckboxLabel={t(`${CHILD_CONFIRM}.CONFIRMATION_CHECKBOX`)}
            upfrontErrors={upfrontErrors}
            upfrontErrorsTitle={t(`${CHILD_CONFIRM}.VALIDATION_FAILED_TITLE`)}
          />
        ),
        isValid: scheduledConfirmed,
        subtitle: t(`${CHILD_CONFIRM}.PAGE_SUBTITLE`)
      }
    ],
    [
      credentials,
      onCredentialsChange,
      credentialsLabels,
      scheduledConfirmed,
      onScheduledConfirmedChange,
      summaryRows,
      actions,
      upfrontErrors,
      t
    ]
  );

  return (
    <AssociationWizard
      steps={steps}
      cancelLabel={t('buttons.CANCEL')}
      nextLabel={t(`${SEMI}.NEXT`)}
      previousLabel={t(`${CHILD_WIZARD}.PREVIOUS`)}
      finishLabel={t(`${SEMI}.FINISH`)}
      onBack={onBack}
      onFinish={handleFinish}
      isFinishing={isFinishing}
      onStepChange={onStepChange}
    />
  );
};

export default MspParentSemiWizard;
